import React, { useEffect, useState, useCallback } from "react";
import { Toaster, toast } from "sonner";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill, Button, Input, TextArea } from "../../components/common/Primitives";
import api, { formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/datetime";
import { Plus, Trash2, ShieldCheck, X, Search, Building2, Save } from "lucide-react";

const STAGES = ["Pre-Seed", "Seed", "Series A", "Series B", "Series C", "Series D+", "Debt", "Other"];
const STATUSES = ["Active", "Exited", "Written-Off"];

export default function InvestorTrackRecord() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/investor/track-record");
      setItems(r.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalDeployed = items.reduce((s, r) => s + (r.amount_inr || 0), 0);
  const active = items.filter((r) => (r.current_status || "Active") === "Active").length;
  const exited = items.filter((r) => r.current_status === "Exited");

  const save = async (draft) => {
    setBusy(true);
    try {
      const body = {
        company_name: draft.company_name,
        company_cin: draft.company_cin || null,
        sector: draft.sector || null,
        invested_at: draft.invested_at || null,
        round_stage: draft.round_stage || null,
        amount_inr: draft.amount_inr ? Number(draft.amount_inr) : null,
        valuation_inr: draft.valuation_inr ? Number(draft.valuation_inr) : null,
        stake_pct: draft.stake_pct ? Number(draft.stake_pct) : null,
        current_status: draft.current_status || "Active",
        exit_multiple: draft.exit_multiple ? Number(draft.exit_multiple) : null,
        notes: draft.notes || null,
        source_metadata: draft.source_metadata || null,
      };
      if (draft.investment_id) {
        await api.patch(`/investor/track-record/${draft.investment_id}`, body);
        toast.success("Updated");
      } else {
        await api.post("/investor/track-record", body);
        toast.success("Added");
      }
      setEditing(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this investment from your track record?")) return;
    setBusy(true);
    try {
      await api.delete(`/investor/track-record/${id}`);
      toast.success("Removed");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <DashboardShell>
      <Toaster richColors position="top-right" />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="overline">TRACK RECORD</div>
            <h1 className="font-display text-3xl lg:text-4xl mt-1">Prior investments</h1>
            <div className="text-sm text-ink-soft mt-1">
              Populate your investment history — founders and VenturaZ diligence weigh verified deployments heavily.
            </div>
          </div>
          <Button variant="gold" onClick={() => setEditing({
            company_name: "", company_cin: "", sector: "", invested_at: "",
            round_stage: "Seed", amount_inr: "", valuation_inr: "", stake_pct: "",
            current_status: "Active", exit_multiple: "", notes: "",
          })} data-testid="track-add">
            <Plus size={14} className="inline mr-1" /> Add investment
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatTile label="Investments" value={items.length} />
          <StatTile label="Total deployed" value={`₹${totalDeployed.toLocaleString("en-IN")}`} />
          <StatTile label="Active" value={active} />
          <StatTile label="Exits" value={exited.length} />
        </div>

        <Card testId="track-list">
          {loading ? (
            <div className="text-center py-8 text-ink-muted text-sm">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-10">
              <div className="font-heading text-lg text-ink mb-1">No investments logged yet</div>
              <div className="text-sm text-ink-soft mb-4">Start with your most recent cheque — it takes 30 seconds.</div>
              <Button variant="gold" onClick={() => setEditing({
                company_name: "", company_cin: "", sector: "", invested_at: "",
                round_stage: "Seed", amount_inr: "", valuation_inr: "", stake_pct: "",
                current_status: "Active", exit_multiple: "", notes: "",
              })} data-testid="track-empty-add">
                <Plus size={14} className="inline mr-1" /> Add your first
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-navy/[0.06]">
              {items.map((r) => (
                <div key={r.investment_id} className="py-4 flex items-start justify-between gap-3" data-testid={`track-${r.investment_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <div className="font-heading text-ink">{r.company_name}</div>
                      {r.round_stage && <Pill tone="muted">{r.round_stage}</Pill>}
                      {r.current_status && (
                        <Pill tone={r.current_status === "Exited" ? "success" : r.current_status === "Written-Off" ? "danger" : "gold"}>
                          {r.current_status}
                        </Pill>
                      )}
                      {r.verified && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-emerald-800 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full" data-testid={`track-verified-${r.investment_id}`}>
                          <ShieldCheck size={10} /> Verified
                        </span>
                      )}
                    </div>
                    <div className="text-xs font-mono text-ink-muted">
                      {r.company_cin && <>CIN {r.company_cin} · </>}
                      {r.sector && <>{r.sector} · </>}
                      {r.invested_at && <>{formatDate(r.invested_at)}</>}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-2 text-sm">
                      <div>
                        <div className="text-[10px] font-mono uppercase text-ink-muted">Invested</div>
                        <div className="font-heading text-ink">₹{(r.amount_inr || 0).toLocaleString("en-IN")}</div>
                      </div>
                      <div>
                        <div className="text-[10px] font-mono uppercase text-ink-muted">Valuation</div>
                        <div className="font-heading text-ink">{r.valuation_inr ? `₹${r.valuation_inr.toLocaleString("en-IN")}` : "—"}</div>
                      </div>
                      <div>
                        <div className="text-[10px] font-mono uppercase text-ink-muted">Stake</div>
                        <div className="font-heading text-ink">{r.stake_pct ? `${r.stake_pct}%` : "—"}</div>
                      </div>
                      {r.current_status === "Exited" && (
                        <div>
                          <div className="text-[10px] font-mono uppercase text-ink-muted">Exit multiple</div>
                          <div className="font-heading text-ink">{r.exit_multiple ? `${r.exit_multiple}x` : "—"}</div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <button onClick={() => setEditing(r)}
                      className="px-2 py-1 rounded border border-navy/15 text-[11px] font-heading hover:border-emerald-500"
                      data-testid={`track-edit-${r.investment_id}`}>Edit</button>
                    <button onClick={() => remove(r.investment_id)}
                      className="px-2 py-1 rounded border border-rose-300 text-[11px] font-heading text-rose-700 hover:bg-rose-50"
                      data-testid={`track-delete-${r.investment_id}`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {editing && (
        <InvestmentEditor
          draft={editing}
          busy={busy}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={() => save(editing)}
        />
      )}
    </DashboardShell>
  );
}

function StatTile({ label, value }) {
  return (
    <div className="rounded-lg border border-navy/10 bg-white p-3">
      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">{label}</div>
      <div className="font-heading text-ink text-xl mt-1">{value}</div>
    </div>
  );
}

function InvestmentEditor({ draft, onChange, onSave, onCancel, busy }) {
  const patch = (k) => (e) => onChange({ ...draft, [k]: e.target.value });
  const [query, setQuery] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [searching, setSearching] = useState(false);

  const runLookup = async () => {
    if ((query || "").trim().length < 3) {
      toast.info("Type at least 3 characters");
      return;
    }
    setSearching(true);
    try {
      const r = await api.post("/investor/track-record/lookup", { query: query.trim() });
      setCandidates(r.data?.results || []);
      if ((r.data?.results || []).length === 0) toast.info("No matches — enter details manually.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSearching(false); }
  };

  const pickCandidate = async (c) => {
    // Fill baseline fields immediately, then optionally fetch full profile.
    onChange({
      ...draft,
      company_name: c.company_legal_name,
      company_cin: c.cin,
      sector: c.sector || draft.sector,
      source_metadata: c,
    });
    setCandidates([]);
    setQuery("");
    // Full lookup (best-effort)
    try {
      const r = await api.post("/investor/track-record/lookup", { cin: c.cin });
      if (r.data?.outcome === "found" && r.data?.company) {
        const co = r.data.company;
        onChange((prev) => ({
          ...prev,
          company_name: co.company_legal_name || prev.company_name,
          company_cin: co.cin || prev.company_cin,
          sector: co.industrial_activity || prev.sector,
          source_metadata: { ...prev.source_metadata, full: co },
        }));
      }
    } catch (_) { /* silently ignore */ }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="track-editor">
      <div className="bg-white rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between border-b border-navy/10 px-6 py-4">
          <div>
            <div className="overline">{draft.investment_id ? "Edit investment" : "New investment"}</div>
            <div className="text-sm text-ink-soft">Auto-fill from MCA where possible.</div>
          </div>
          <button onClick={onCancel} className="text-ink-muted hover:text-ink" data-testid="track-editor-close">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {!draft.investment_id && (
            <div className="border border-emerald-200 bg-emerald-50/50 rounded-md p-3">
              <div className="text-xs font-heading text-emerald-800 mb-2 inline-flex items-center gap-1">
                <Search size={12} /> Search company (MCA)
              </div>
              <div className="flex items-center gap-2">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runLookup(); } }}
                  placeholder="Type company name (min 3 chars) or paste CIN"
                  className="flex-1 px-3 py-2 rounded border border-navy/15 text-sm"
                  data-testid="track-lookup-input"
                />
                <Button variant="gold" onClick={runLookup} disabled={searching}
                  type="button" data-testid="track-lookup-btn">
                  {searching ? "Searching…" : "Search"}
                </Button>
              </div>
              {candidates.length > 0 && (
                <div className="mt-3 divide-y divide-emerald-200/60 border border-emerald-200 rounded-md bg-white max-h-56 overflow-auto" data-testid="track-lookup-results">
                  {candidates.map((c) => (
                    <button
                      key={c.cin}
                      type="button"
                      onClick={() => pickCandidate(c)}
                      className="w-full text-left px-3 py-2 hover:bg-emerald-50 flex items-start gap-2"
                      data-testid={`track-lookup-pick-${c.cin}`}
                    >
                      <Building2 size={14} className="text-emerald-800 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="font-heading text-ink text-sm">{c.company_legal_name}</div>
                        <div className="text-[11px] font-mono text-ink-muted">
                          {c.cin} · {c.company_status || "—"} · {c.registered_state || "—"}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="Company name" value={draft.company_name} onChange={patch("company_name")}
              testId="track-company" />
            <Input label="CIN (optional)" value={draft.company_cin} onChange={patch("company_cin")}
              testId="track-cin" />
            <Input label="Sector" value={draft.sector} onChange={patch("sector")} testId="track-sector" />
            <Input label="Invested date" type="date" value={draft.invested_at} onChange={patch("invested_at")}
              testId="track-date" />
            <div>
              <label className="text-[11px] font-heading uppercase tracking-widest text-ink-muted">Stage</label>
              <select value={draft.round_stage || "Seed"} onChange={patch("round_stage")}
                className="mt-1 w-full px-3 py-2 rounded border border-navy/15 text-sm bg-white"
                data-testid="track-stage">
                {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-heading uppercase tracking-widest text-ink-muted">Status</label>
              <select value={draft.current_status || "Active"} onChange={patch("current_status")}
                className="mt-1 w-full px-3 py-2 rounded border border-navy/15 text-sm bg-white"
                data-testid="track-status">
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <Input label="Amount invested (₹)" type="number" value={draft.amount_inr}
              onChange={patch("amount_inr")} testId="track-amount" />
            <Input label="Post-money valuation (₹)" type="number" value={draft.valuation_inr}
              onChange={patch("valuation_inr")} testId="track-valuation" />
            <Input label="Stake (%)" type="number" step="0.01" value={draft.stake_pct}
              onChange={patch("stake_pct")} testId="track-stake" />
            {draft.current_status === "Exited" && (
              <Input label="Exit multiple (x)" type="number" step="0.01" value={draft.exit_multiple}
                onChange={patch("exit_multiple")} testId="track-exit" />
            )}
          </div>
          <TextArea label="Notes" value={draft.notes} onChange={patch("notes")} rows={2}
            testId="track-notes" />
        </div>

        <div className="border-t border-navy/10 px-6 py-4 flex items-center justify-end gap-2">
          <Button variant="outline" type="button" onClick={onCancel} data-testid="track-cancel">Cancel</Button>
          <Button variant="gold" type="button" onClick={onSave} disabled={busy || !draft.company_name}
            data-testid="track-save">
            <Save size={14} className="inline mr-1" /> {busy ? "Saving…" : "Save investment"}
          </Button>
        </div>
      </div>
    </div>
  );
}
